import { getTestTypeConfig } from '@/config/testTypeConfig'
import {
  guardianContactHasSubstantiveContact,
  type CanonicalOrderPayload,
  type CanonicalParentInfo,
  type Comparator,
  type FamilyMemberRelationship,
} from '@/ordering/canonicalOrder'
import { formatComparatorRelationshipLabel } from '@/ordering/comparatorRelationshipOptions'
import { parentSideForComparator } from '@/ordering/comparatorWriteAdapter'
import { requiredComparatorFields } from '@/ordering/requiredComparatorFields'
import {
  ClinicalData,
  FamilyHistoryData,
  OrderData,
  ParentFieldShape,
  PatientData,
  PhenotypicTrait,
  ReasonForTestingData,
  RequisitionFormData,
  TestType,
} from '@/types/RequisitionFormTypes'

/**
 * P6 (MOTHER/FATHER migration): iterate `canonical.comparators[]` directly
 * by relationship role. Replaces `resolveParentForRead(co, side)` inside
 * FormCompletionChecker so the predicates no longer go through the slot-view
 * shim (which P7 deletes). Inbound payloads have their legacy `mother`/
 * `father` slots folded into `comparators[]` by
 * `normalizeInboundParentComparators` at the slice boundary, so iterating the
 * list here is observably equivalent to the old shim path.
 */
function findParentComparatorByRole(
  canonical: CanonicalOrderPayload | null | undefined,
  role: 'mother' | 'father'
): Comparator | undefined {
  if (!canonical || !Array.isArray(canonical.comparators)) return undefined
  return canonical.comparators.find((c) => c != null && parentSideForComparator(c) === role)
}

/**
 * Phase C (MOTHER/FATHER migration): the FCC canonical predicates take the
 * selected-comparator relationship list (the source of truth on
 * `TestOptions.selectedComparatorRelationships`) instead of the retired
 * `biologicalMotherSelected` / `biologicalFatherSelected` boolean pair. The
 * predicates only branch on MOTHER / FATHER presence, so this folds the list
 * down to those two flags; any other relationships in the list are ignored,
 * mirroring the previous boolean-pair semantics exactly.
 */
function parentSelectionFlags(selectedComparatorRelationships: readonly FamilyMemberRelationship[] | undefined): {
  mSel: boolean
  fSel: boolean
} {
  const list = selectedComparatorRelationships ?? []
  return { mSel: list.includes('MOTHER'), fSel: list.includes('FATHER') }
}

/**
 * Phenotypic trait ("clinically affected status") is a required question, but
 * every enum value — including `Unknown` — is a valid answer. Because the
 * comparator modal collapses an Unknown selection to `null` on save, a
 * completed parent whose status is Unknown reaches the page-level checks with
 * `phenotypicTrait == null`. Both `null`/`undefined` (persisted "Unknown") and
 * any recognized enum value therefore count as answered; only an unrecognized
 * junk value would fail. This keeps "Unknown" a supported indication status
 * rather than forcing the user to pick Affected/Unaffected/Partially affected.
 */
function isPhenotypicTraitAnswered(value: ParentFieldShape['phenotypicTrait']): boolean {
  if (value == null) return true
  const trimmed = String(value).trim()
  if (trimmed === '') return true
  return (
    trimmed === PhenotypicTrait.Affected ||
    trimmed === PhenotypicTrait.Unaffected ||
    trimmed === PhenotypicTrait.PartiallyAffected ||
    trimmed === PhenotypicTrait.Unknown
  )
}

export class FormCompletionChecker {
  static isOrderComplete(data: OrderData): boolean {
    return Boolean(data.selectedOption && data.selectedOption.trim() !== '')
  }

  static isPatientComplete(data: PatientData): boolean {
    return Boolean(
      data.firstName &&
      data.firstName.trim() !== '' &&
      data.lastName &&
      data.lastName.trim() !== '' &&
      data.dateOfBirth &&
      data.dateOfBirth.trim() !== '' &&
      data.sex &&
      data.sex.trim() !== '' &&
      data.mrn &&
      data.mrn.trim() !== '' &&
      data.collectionType &&
      data.collectionType.trim() !== ''
    )
  }

  static isClinicalComplete(data: ClinicalData): boolean {
    return Boolean(data.clinicalNote && data.clinicalNote.trim() !== '')
  }

  static hasReasonData(data: ReasonForTestingData): boolean {
    // Helper function to check if clinical diagnosis has content
    const hasClinicalDiagnosis = () => {
      if (Array.isArray(data.clinicalDiagnosis)) {
        return data.clinicalDiagnosis.length > 0
      }
      return false
    }

    const clinicalDiagnosis = Boolean(hasClinicalDiagnosis())
    const icd10Diagnosis = Boolean(
      (Array.isArray(data.icd10Diagnosis) && data.icd10Diagnosis.length > 0) ||
      (typeof data.icd10Diagnosis === 'string' && data.icd10Diagnosis.trim() !== '')
    )

    return clinicalDiagnosis || icd10Diagnosis
  }

  static isReasonComplete(data: ReasonForTestingData): boolean {
    const userInteracted = Boolean(data.userInteracted)
    return userInteracted && this.hasReasonData(data)
  }

  static isFamilyComplete(_data: FamilyHistoryData): boolean {
    // Family History is optional - always valid for form submission
    return true
  }

  static hasFamilyData(data: FamilyHistoryData): boolean {
    // For UI indicators - returns true only if user entered data
    const hasText = Boolean(data.familyHistory && data.familyHistory.trim() !== '')
    const hasCheckbox = data.hasSibling || data.hasParent || data.hasGrandparent || data.hasCousin
    return hasText || hasCheckbox
  }

  /**
   * Check if parent data has any fields filled (user started filling the form)
   * Returns true only if user has entered any data - excludes default/Unknown values
   */
  static hasParentDataStarted(data?: ParentFieldShape): boolean {
    if (!data) {
      return false
    }

    // Check text fields - user has entered any text
    const hasTextData =
      (data.parentFirstName && data.parentFirstName.trim() !== '') ||
      (data.parentLastName && data.parentLastName.trim() !== '') ||
      (data.parentDateOfBirth && data.parentDateOfBirth.trim() !== '') ||
      (data.parentMRN && data.parentMRN.trim() !== '') ||
      (data.parentPhone && data.parentPhone.trim() !== '') ||
      (data.parentEmail && data.parentEmail.trim() !== '')

    // Check selection fields - user has selected a non-default value.
    // PhenotypicTrait.Unknown is a valid explicit choice for completion, but we still treat it
    // like the legacy empty default here so an untouched row is not "started" solely because
    // the canonical payload defaulted phenotypicTrait to unknown.
    // Note: CollectionType.Unknown is an empty string (''), so the truthiness check excludes it.
    const hasSelectedPhenotypicTrait =
      data.phenotypicTrait &&
      data.phenotypicTrait !== PhenotypicTrait.Unknown &&
      String(data.phenotypicTrait).trim() !== ''

    const hasSelectedCollectionType = data.collectionType && data.collectionType.trim() !== ''

    // Check HPO terms - user has added any terms
    const hasHPOTerms = Array.isArray(data.hpoTerms) && data.hpoTerms.length > 0

    return Boolean(hasTextData || hasSelectedPhenotypicTrait || hasSelectedCollectionType || hasHPOTerms)
  }

  /**
   * Check if parent data is fully complete (all required fields filled).
   * PhenotypicTrait.Unknown counts as a complete selection — it is an explicit enum value on the form.
   */
  static isParentDataFullyComplete(data?: ParentFieldShape): boolean {
    if (!data) {
      return false
    }
    // Phase 2c: derive the required identity/specimen fields from the shared
    // `requiredComparatorFields` schema (the single source of truth the
    // comparator modal also consumes) so the modal and the page cannot
    // diverge on what "complete" means. A tested parent always requires
    // clinical identity + a sample type, so the schema is queried with those
    // flags on — behavior-preserving vs. the prior hard-coded field list.
    const required = requiredComparatorFields({
      isTesting: true,
      enableSampleType: true,
      requiresClinicalIdentity: true,
    })
    return Boolean(
      (!required.has('firstName') || (data.parentFirstName && data.parentFirstName.trim() !== '')) &&
      (!required.has('lastName') || (data.parentLastName && data.parentLastName.trim() !== '')) &&
      (!required.has('dateOfBirth') || (data.parentDateOfBirth && data.parentDateOfBirth.trim() !== '')) &&
      // Sex assigned at birth is required. Legacy parent flows derive it
      // implicitly from `parentType` (mother/father), so a row with a
      // `parentType` satisfies it; the comparator-flattened path must carry an
      // explicit `parentSex` (see `comparatorAsParentData`).
      (!required.has('sex') || (data.parentSex && data.parentSex.trim() !== '') || Boolean(data.parentType)) &&
      // Phenotypic trait is a required question, but "Unknown" is a valid
      // answer. The modal collapses an Unknown selection to `null` on save
      // (see ComparatorModal.draftToComparator), so a completed parent whose
      // clinical status is Unknown arrives here with `phenotypicTrait == null`.
      // Treat any value — including `null`/`undefined`/`Unknown` — as a
      // satisfied answer; the question is answered, the answer is just
      // "Unknown". (Requiring a truthy, non-Unknown value here was the bug
      // that forced clinically-affected status to be picked.)
      (!required.has('phenotypicTrait') || isPhenotypicTraitAnswered(data.phenotypicTrait)) &&
      (!required.has('sampleType') || (data.collectionType && data.collectionType.trim() !== ''))
    )
  }

  /**
   * Check if biological parent section is complete.
   * Returns true only if user started AND completed all required fields.
   * Returns false if section is untouched (for UI indicator purposes).
   */
  static isBiologicalParentComplete(data?: ParentFieldShape): boolean {
    if (!data) {
      return false // No data means not complete (untouched)
    }

    // If user hasn't started filling the form, not complete
    if (!this.hasParentDataStarted(data)) {
      return false
    }

    // If user started filling, validate all required fields
    return this.isParentDataFullyComplete(data)
  }

  /**
   * Check if biological parent section is valid for form submission.
   * - If no data was entered, section is valid (optional)
   * - If user started entering data, all required fields must be filled
   */
  static isBiologicalParentValid(data?: ParentFieldShape): boolean {
    if (!data) {
      return true // No data means section is optional and valid
    }

    // If user hasn't started filling the form, it's valid (optional)
    if (!this.hasParentDataStarted(data)) {
      return true
    }

    // If user started filling, validate all required fields
    return this.isParentDataFullyComplete(data)
  }

  static isConsentComplete(): boolean {
    // Special case for consent (temporarily bypassed)
    return true
  }

  /**
   * Check if parent section is complete based on test type configuration.
   * Uses requiresParentSamples and parentSamplesRequired from config.
   *
   * Logic:
   * - If requiresParentSamples is false: parent sections are completely optional
   * - If parentSamplesRequired is 2: both parents must be complete
   * - If parentSamplesRequired is 1: at least one parent must be complete
   * - If user starts filling a section, all required fields must be completed
   */
  static isParentSectionComplete(section: 'mother' | 'father', formData: RequisitionFormData): boolean {
    const selectedTestType = formData.order.selectedOption as TestType
    const testTypeConfig = getTestTypeConfig(selectedTestType)

    // If no config found or parent samples not required, use optional validation
    if (!testTypeConfig || !testTypeConfig.requiresParentSamples) {
      // Parent sections are optional - use validation logic (returns true for untouched)
      const targetParent = section === 'mother' ? formData.mother : formData.father
      return this.isBiologicalParentValid(targetParent)
    }

    const { parentSamplesRequired } = testTypeConfig
    const motherData = formData.mother
    const fatherData = formData.father

    // Check completion status of each parent
    const isMotherComplete = this.isParentDataFullyComplete(motherData)
    const isFatherComplete = this.isParentDataFullyComplete(fatherData)
    const motherStarted = this.hasParentDataStarted(motherData)
    const fatherStarted = this.hasParentDataStarted(fatherData)

    // If 2 parents required: both must be complete
    if (parentSamplesRequired === 2) {
      const isTargetComplete = section === 'mother' ? isMotherComplete : isFatherComplete
      const targetStarted = section === 'mother' ? motherStarted : fatherStarted

      // If user started filling but not complete, return false
      if (targetStarted && !isTargetComplete) {
        return false
      }

      // This specific section must be complete (required)
      return isTargetComplete
    }

    // If 1 parent required: at least one must be complete
    if (parentSamplesRequired === 1) {
      const isTargetComplete = section === 'mother' ? isMotherComplete : isFatherComplete
      const targetStarted = section === 'mother' ? motherStarted : fatherStarted
      const otherComplete = section === 'mother' ? isFatherComplete : isMotherComplete

      // If user started filling this section, it must be complete
      if (targetStarted && !isTargetComplete) {
        return false
      }

      // If this section is complete, it's valid
      if (isTargetComplete) {
        return true
      }

      // If this section is empty but the other parent is complete, this section is valid (optional)
      if (!targetStarted && otherComplete) {
        return true
      }

      // If neither parent has data and only 1 is required, mark as incomplete
      // (at least one parent needs to be filled)
      return false
    }

    // parentSamplesRequired is 0 - completely optional (use validation logic)
    const targetParent = section === 'mother' ? motherData : fatherData
    return this.isBiologicalParentValid(targetParent)
  }

  /**
   * Check if a section has been completed (for UI indicators).
   * Returns true only when user entered data AND section is complete.
   * Unlike isSectionComplete which is for form validation.
   */
  static hasSectionCompleted(section: keyof RequisitionFormData, formData: RequisitionFormData): boolean {
    switch (section) {
      case 'family':
        return this.hasFamilyData(formData.family)
      case 'mother':
      case 'father': {
        const targetParent = section === 'mother' ? formData.mother : formData.father
        return this.isBiologicalParentComplete(targetParent)
      }
      default:
        // For non-optional sections, use regular completion check
        return this.isSectionComplete(section, formData)
    }
  }

  static isSectionComplete(section: keyof RequisitionFormData, formData: RequisitionFormData): boolean {
    switch (section) {
      case 'order':
        return this.isOrderComplete(formData.order)
      case 'patient':
        return this.isPatientComplete(formData.patient)
      case 'clinical':
        return this.isClinicalComplete(formData.clinical)
      case 'reason':
        return this.isReasonComplete(formData.reason)
      case 'family':
        return this.isFamilyComplete(formData.family)
      case 'mother':
      case 'father':
        // Use config-driven validation for parent sections
        return this.isParentSectionComplete(section, formData)
      case 'consent':
        return this.isConsentComplete()
      default:
        return false
    }
  }

  // ─── Canonical-native completion checks ──────────────────────────────────────

  /**
   * Check whether a parent section is complete given a CanonicalParentInfo block.
   * CanonicalParentInfo shares the same field names as ParentFieldShape so the
   * existing TRF-based checkers can be safely applied via a cast.
   */
  static isCanonicalParentComplete(parent: CanonicalParentInfo | null | undefined): boolean {
    return this.isBiologicalParentComplete(parent as ParentFieldShape | undefined)
  }

  static isCanonicalParentValid(parent: CanonicalParentInfo | null | undefined): boolean {
    return this.isBiologicalParentValid(parent as ParentFieldShape | undefined)
  }

  static hasCanonicalParentData(parent: CanonicalParentInfo | null | undefined): boolean {
    return this.hasParentDataStarted(parent as ParentFieldShape | undefined)
  }

  /**
   * Shared accessor that returns the resolved Comparator row for a parent
   * side by iterating `canonical.comparators[]` and matching the relationship
   * literal (P6, MOTHER/FATHER migration).
   *
   * Page-level consumers (`OrderInProgress.tsx`, `OrderStart.tsx`,
   * `OrderResultReady.tsx`, `PDFRenderer.ts`) memoize this view once per
   * render and pass it to the comparator-aware predicates below; P8 replaces
   * the per-side caller pattern with a list-driven editor.
   *
   * @deprecated MOTHER/FATHER migration: prefer iterating
   *   `canonical.comparators[]` filtered by `parentSideForComparator(c)`
   *   directly. P8 removes the per-side call sites; P11/P12 will retire this
   *   accessor once the UI is list-driven.
   */
  static readParent(
    canonical: CanonicalOrderPayload | null | undefined,
    side: 'mother' | 'father'
  ): Comparator | undefined {
    return findParentComparatorByRole(canonical, side)
  }

  /**
   * C1.2: comparator-shaped equivalent of
   * {@link canonicalParentHasContactChannelForResearch}. Reads from the
   * comparator's nested `demographic.{email,phone}` instead of the legacy
   * slot's flat `parentEmail` / `parentPhone`. C1.1: public for page-level
   * "missing research contact channel" prompts.
   */
  static comparatorHasContactChannel(c: Comparator | null | undefined): boolean {
    if (!c) return false
    const e = (c.demographic?.email ?? '').trim()
    const ph = (c.demographic?.phone ?? '').trim()
    return Boolean(e || ph)
  }

  /**
   * C1.2: comparator-shaped completeness/started predicates. Maps a
   * `Comparator` onto the per-field shape `isParentDataFullyComplete` and
   * `hasParentDataStarted` already handle (ParentFieldShape) so the
   * existing TRF-tier validators stay the single source of truth for what
   * "fully complete" / "started" means. Demographic fields land on
   * `parent*` keys, clinical/HPO and sample type land at the top level.
   * C1.1: public so callers can flatten a Comparator before invoking the
   * existing `isBiologicalParentComplete` / `hasParentDataStarted` predicates.
   */
  static comparatorAsParentData(c: Comparator | null | undefined): ParentFieldShape | undefined {
    if (!c) return undefined
    const d = c.demographic
    return {
      parentFirstName: d?.firstName,
      parentLastName: d?.lastName,
      parentDateOfBirth: d?.dateOfBirth,
      parentSex: d?.sex,
      parentMRN: d?.mrn,
      parentEmail: d?.email,
      parentPhone: d?.phone,
      phenotypicTrait: c.phenotypicTrait as ParentFieldShape['phenotypicTrait'],
      collectionType: c.specimen?.collectionType ?? undefined,
      hpoTerms: c.hpoTerms,
    } as ParentFieldShape
  }

  /**
   * Config-driven parent completion check operating directly on canonical data.
   * Mirrors isParentSectionComplete but takes a CanonicalOrderPayload + resolved
   * parentSamplesRequired instead of a full RequisitionFormData.
   */
  static isCanonicalParentSectionComplete(
    section: 'mother' | 'father',
    canonical: CanonicalOrderPayload,
    parentSamplesRequired: number
  ): boolean {
    if (parentSamplesRequired === 0) {
      const target = findParentComparatorByRole(canonical, section)
      return this.isBiologicalParentValid(this.comparatorAsParentData(target))
    }

    const motherBpd = this.comparatorAsParentData(findParentComparatorByRole(canonical, 'mother'))
    const fatherBpd = this.comparatorAsParentData(findParentComparatorByRole(canonical, 'father'))
    const isMotherComplete = this.isParentDataFullyComplete(motherBpd)
    const isFatherComplete = this.isParentDataFullyComplete(fatherBpd)
    const motherStarted = this.hasParentDataStarted(motherBpd)
    const fatherStarted = this.hasParentDataStarted(fatherBpd)

    if (parentSamplesRequired >= 2) {
      const targetComplete = section === 'mother' ? isMotherComplete : isFatherComplete
      const targetStarted = section === 'mother' ? motherStarted : fatherStarted
      if (targetStarted && !targetComplete) return false
      return targetComplete
    }

    // parentSamplesRequired === 1
    const targetComplete = section === 'mother' ? isMotherComplete : isFatherComplete
    const targetStarted = section === 'mother' ? motherStarted : fatherStarted
    const otherComplete = section === 'mother' ? isFatherComplete : isMotherComplete
    if (targetStarted && !targetComplete) return false
    if (targetComplete) return true
    if (!targetStarted && otherComplete) return true
    return false
  }

  /**
   * Duo/trio: exactly one tested parent must be marked primary guardian (canonical flag).
   * Used for display / normalization — not used to block order submit.
   * Proband-only orders skip this (parentSamplesRequired === 0).
   */
  static isCanonicalPrimaryGuardianSatisfied(
    canonical: CanonicalOrderPayload | null | undefined,
    parentSamplesRequired: number,
    opts: { selectedComparatorRelationships: readonly FamilyMemberRelationship[] }
  ): boolean {
    if (parentSamplesRequired <= 0) return true
    if (!canonical) return false

    const mPrimary = findParentComparatorByRole(canonical, 'mother')?.isPrimaryGuardian === true
    const fPrimary = findParentComparatorByRole(canonical, 'father')?.isPrimaryGuardian === true

    if (parentSamplesRequired >= 2) {
      return mPrimary !== fPrimary && (mPrimary || fPrimary)
    }

    const { mSel, fSel } = parentSelectionFlags(opts.selectedComparatorRelationships)
    if (mSel && fSel) {
      return mPrimary !== fPrimary && (mPrimary || fPrimary)
    }
    if (mSel) return mPrimary
    if (fSel) return fPrimary
    return false
  }

  /**
   * Comparator parent row has at least one non-empty email or phone (CareBus research contact channel).
   */
  static canonicalParentHasContactChannelForResearch(parent: CanonicalParentInfo | null | undefined): boolean {
    if (!parent) return false
    const e = (parent.parentEmail ?? '').trim()
    const ph = (parent.parentPhone ?? '').trim()
    return Boolean(e || ph)
  }

  /**
   * Which biological parent is primary guardian for duo/trio, when flags are consistent.
   */
  static resolvePrimaryGuardianParentKey(
    canonical: CanonicalOrderPayload,
    parentSamplesRequired: number,
    opts: { selectedComparatorRelationships: readonly FamilyMemberRelationship[] }
  ): 'mother' | 'father' | null {
    if (parentSamplesRequired <= 0) return null
    const m = findParentComparatorByRole(canonical, 'mother')
    const f = findParentComparatorByRole(canonical, 'father')
    if (parentSamplesRequired >= 2) {
      if (m?.isPrimaryGuardian === true && f?.isPrimaryGuardian !== true) return 'mother'
      if (f?.isPrimaryGuardian === true && m?.isPrimaryGuardian !== true) return 'father'
      return null
    }
    const { mSel, fSel } = parentSelectionFlags(opts.selectedComparatorRelationships)
    if (mSel && m?.isPrimaryGuardian === true) return 'mother'
    if (fSel && f?.isPrimaryGuardian === true) return 'father'
    return null
  }

  // ─── Generic (relationship-agnostic) primary-guardian helpers ────────────────
  // P-item #5: primary-guardian is a single designation across the whole
  // `comparators[]` list (`Comparator.isPrimaryGuardian`), independent of the
  // legacy MOTHER / FATHER slot keying. These iterate every comparator
  // (parents, siblings, "other comp", etc.) so the R5 / list-driven flow does
  // not assume the guardian is one of two fixed parent slots. The mother/father
  // -keyed `resolvePrimaryGuardianParentKey` / `isCanonicalPrimaryGuardianSatisfied`
  // above remain for the two-card UI + legacy submit gate until that UI becomes
  // list-driven (audit item #6).

  /** Every comparator flagged as the primary guardian (any relationship). */
  static primaryGuardianComparators(canonical: CanonicalOrderPayload | null | undefined): Comparator[] {
    if (!canonical || !Array.isArray(canonical.comparators)) return []
    return canonical.comparators.filter((c): c is Comparator => c != null && c.isPrimaryGuardian === true)
  }

  /**
   * The single primary-guardian comparator, or `undefined` when none (or more
   * than one) is designated. The canonical-order slice enforces the
   * single-primary-guardian invariant on write, so the >1 case is defensive.
   */
  static resolvePrimaryGuardianComparator(canonical: CanonicalOrderPayload | null | undefined): Comparator | undefined {
    const pgs = this.primaryGuardianComparators(canonical)
    return pgs.length === 1 ? pgs[0] : undefined
  }

  /**
   * Detects when two or more participants in the order (the proband plus any
   * comparators — biological parents, siblings, "other", etc.) share the same
   * MRN. Every participant must carry a unique medical record number, so a
   * shared MRN is a hard block on submit / update.
   *
   * MRNs are compared case-insensitively after trimming; blank/absent MRNs are
   * ignored (they are not a collision). PROBAND-typed comparator rows are
   * skipped so the proband is not compared against a duplicate of itself. When
   * a collision is found the first two colliding participant labels are
   * returned so callers can surface a specific message.
   */
  static findCanonicalOrderMrnCollision(
    canonical: CanonicalOrderPayload | null | undefined
  ): { mrn: string; labels: [string, string] } | undefined {
    if (!canonical) return undefined
    const participants: Array<{ label: string; mrn: string }> = []
    const push = (label: string, rawMrn: string | null | undefined) => {
      const mrn = (rawMrn ?? '').trim()
      if (mrn) participants.push({ label, mrn })
    }
    push('patient', canonical.patient?.mrn)
    for (const c of canonical.comparators ?? []) {
      if (c == null || c.relationship === 'PROBAND') continue
      const label = formatComparatorRelationshipLabel(c.relationship) || 'comparator'
      push(label, c.demographic?.mrn)
    }
    const seen = new Map<string, string>()
    for (const { label, mrn } of participants) {
      const key = mrn.toLowerCase()
      const priorLabel = seen.get(key)
      if (priorLabel !== undefined) {
        return { mrn, labels: [priorLabel, label] }
      }
      seen.set(key, label)
    }
    return undefined
  }

  /** True when any two participants in the order share the same MRN. */
  static canonicalOrderHasMrnCollision(canonical: CanonicalOrderPayload | null | undefined): boolean {
    return this.findCanonicalOrderMrnCollision(canonical) !== undefined
  }

  /**
   * A primary-guardian comparator exists and carries at least one substantive
   * contact/identity field (name / email / phone). Used by the R5 decoupled
   * submit gate to satisfy the standalone-guardian requirement — a
   * primary-guardian comparator owns the guardian info once the legacy
   * `guardianContact` slot is nulled.
   */
  static primaryGuardianComparatorHasContact(canonical: CanonicalOrderPayload | null | undefined): boolean {
    return this.primaryGuardianComparators(canonical).some((c) =>
      [c.demographic?.firstName, c.demographic?.lastName, c.demographic?.email, c.demographic?.phone].some(
        (v) => v != null && String(v).trim() !== ''
      )
    )
  }

  /**
   * A primary-guardian comparator exists and carries a research-contact channel
   * (email OR phone). Stricter than {@link primaryGuardianComparatorHasContact}
   * (which accepts a name-only row): once the proband opts into research
   * contact, the primary guardian must supply a reachable channel, so the
   * guardian-contact card is only "satisfied" when that channel is present.
   */
  static primaryGuardianComparatorHasContactChannel(canonical: CanonicalOrderPayload | null | undefined): boolean {
    return this.primaryGuardianComparators(canonical).some((c) => this.comparatorHasContactChannel(c))
  }

  /**
   * When proband opted into research contact: which comparator row must supply email/phone for CareBus
   * (primary guardian row, or either row when PG unset until one qualifies). Used by parent modals.
   */
  static canonicalComparatorRowObligatedForProbandResearchContact(
    role: 'mother' | 'father',
    canonical: CanonicalOrderPayload | null | undefined,
    options: {
      parentSamplesRequired: number
      selectedComparatorRelationships: readonly FamilyMemberRelationship[]
    }
  ): boolean {
    if (!canonical || canonical.patient?.researchContactConsent !== true) return false
    const psr = options.parentSamplesRequired
    const { mSel, fSel } = parentSelectionFlags(options.selectedComparatorRelationships)
    const checkMotherSlot = psr >= 2 || mSel
    const checkFatherSlot = psr >= 2 || fSel

    if (role === 'mother' && !checkMotherSlot) return false
    if (role === 'father' && !checkFatherSlot) return false

    const pg = this.resolvePrimaryGuardianParentKey(canonical, psr, {
      selectedComparatorRelationships: options.selectedComparatorRelationships,
    })
    const mom = findParentComparatorByRole(canonical, 'mother')
    const dad = findParentComparatorByRole(canonical, 'father')
    const fatherHas = checkFatherSlot && this.comparatorHasContactChannel(dad)
    const motherHas = checkMotherSlot && this.comparatorHasContactChannel(mom)

    if (role === 'mother') {
      if (pg === 'mother') return true
      if (pg === 'father') return false
      return !fatherHas
    }
    if (pg === 'father') return true
    if (pg === 'mother') return false
    return !motherHas
  }

  /**
   * Parent summary cards (OrderStart): when to show “Add email or phone for research contact” on one row.
   * Mirrors {@link isCanonicalComparatorResearchContactSatisfied}: primary guardian supplies proband opt-in contact;
   * when PG is unset, any one tested parent with a channel suffices — do not flag the other parent once satisfied.
   */
  static canonicalParentNeedsResearchContactChannelPrompt(
    role: 'mother' | 'father',
    canonical: CanonicalOrderPayload | null | undefined,
    options: {
      parentSamplesRequired: number
      selectedComparatorRelationships: readonly FamilyMemberRelationship[]
    }
  ): boolean {
    if (!canonical) return false
    const psr = options.parentSamplesRequired
    const { mSel, fSel } = parentSelectionFlags(options.selectedComparatorRelationships)
    const patientResearch = canonical.patient?.researchContactConsent === true

    const mom = findParentComparatorByRole(canonical, 'mother')
    const dad = findParentComparatorByRole(canonical, 'father')

    if (role === 'mother') {
      if (!mom || !this.isBiologicalParentComplete(this.comparatorAsParentData(mom))) return false
      if (this.comparatorHasContactChannel(mom)) return false
      if (mom.secondaryConsent?.researchContactConsent === true) return true
      if (!patientResearch) return false
      if (!(psr >= 2 || mSel)) return false
      return this.canonicalComparatorRowObligatedForProbandResearchContact('mother', canonical, options)
    }

    if (!dad || !this.isBiologicalParentComplete(this.comparatorAsParentData(dad))) return false
    if (this.comparatorHasContactChannel(dad)) return false
    if (dad.secondaryConsent?.researchContactConsent === true) return true
    if (!patientResearch) return false
    if (!(psr >= 2 || fSel)) return false
    return this.canonicalComparatorRowObligatedForProbandResearchContact('father', canonical, options)
  }

  /**
   * Duo/trio: require email or phone when a comparator opts into parent-level research contact,
   * or when the proband opts into research contact (CareBus paths). If a primary guardian is
   * selected, their row must have a channel; if not, at least one tested comparator row must have one (trio).
   */
  static isCanonicalComparatorResearchContactSatisfied(
    canonical: CanonicalOrderPayload,
    options: {
      parentSamplesRequired: number
      selectedComparatorRelationships?: readonly FamilyMemberRelationship[]
    }
  ): boolean {
    const psr = options.parentSamplesRequired
    if (psr <= 0) return true
    const { mSel, fSel } = parentSelectionFlags(options.selectedComparatorRelationships)
    const patientResearch = canonical.patient?.researchContactConsent === true

    const checkMotherSlot = psr >= 2 || mSel
    const checkFatherSlot = psr >= 2 || fSel
    const mom = findParentComparatorByRole(canonical, 'mother')
    const dad = findParentComparatorByRole(canonical, 'father')

    if (checkMotherSlot) {
      if (mom?.secondaryConsent?.researchContactConsent === true) {
        if (!this.comparatorHasContactChannel(mom)) return false
      }
    }
    if (checkFatherSlot) {
      if (dad?.secondaryConsent?.researchContactConsent === true) {
        if (!this.comparatorHasContactChannel(dad)) return false
      }
    }

    if (patientResearch) {
      const pg = this.resolvePrimaryGuardianParentKey(canonical, psr, {
        selectedComparatorRelationships: options.selectedComparatorRelationships ?? [],
      })
      if (pg === 'mother' && !this.comparatorHasContactChannel(mom)) {
        return false
      }
      if (pg === 'father' && !this.comparatorHasContactChannel(dad)) {
        return false
      }
      if (pg === null) {
        const motherHas = checkMotherSlot && this.comparatorHasContactChannel(mom)
        const fatherHas = checkFatherSlot && this.comparatorHasContactChannel(dad)
        /** Trio / duo-without-PG: at least one tested parent must have a research contact channel. */
        if (!motherHas && !fatherHas) {
          return false
        }
      }
    }

    return true
  }

  /**
   * When org UI config shows incidental findings, the proband must explicitly choose opt-in or opt-out.
   * Each comparator row that is active for the current test and fully demographic-complete must set
   * {@link CanonicalParentInfo.includeIncidentalFindings} to true or false.
   */
  static isCanonicalIncidentalFindingsSubmittable(
    canonical: CanonicalOrderPayload | null | undefined,
    options: {
      incidentalApplicable: boolean
      parentSamplesRequired: number
      selectedComparatorRelationships?: readonly FamilyMemberRelationship[]
    }
  ): boolean {
    if (!canonical || !options.incidentalApplicable) {
      return true
    }
    const sel = canonical.patient?.incidentalFindingsSelection
    if (sel !== 'opt-in' && sel !== 'opt-out') {
      return false
    }
    const psr = options.parentSamplesRequired
    const { mSel, fSel } = parentSelectionFlags(options.selectedComparatorRelationships)
    const motherSlot = psr >= 2 || mSel
    const fatherSlot = psr >= 2 || fSel

    const parentIfOk = (c: Comparator | null | undefined, slotActive: boolean): boolean => {
      if (!slotActive) {
        return true
      }
      if (!c) {
        return true
      }
      if (!this.isParentDataFullyComplete(this.comparatorAsParentData(c))) {
        return true
      }
      const inc = c.secondaryConsent?.includeIncidentalFindings
      return inc === true || inc === false
    }

    if (!parentIfOk(findParentComparatorByRole(canonical, 'mother'), motherSlot)) {
      return false
    }
    if (!parentIfOk(findParentComparatorByRole(canonical, 'father'), fatherSlot)) {
      return false
    }
    return true
  }

  /**
   * Proband NY sample-retention / research-contact gate. These consents have no
   * completeness requirement today (they default silently), so they are gated
   * ONLY when their config is authored `requireInteraction: true` (already ANDed
   * with applicability by the caller). When gated, the proband must have an
   * explicit `true`/`false` — an untouched (`null`/`undefined`) value blocks
   * submit. Returns true (complete) when neither gate is engaged, preserving
   * today's behavior.
   */
  static isCanonicalSecondaryConsentSubmittable(
    canonical: CanonicalOrderPayload | null | undefined,
    options: {
      nyRetentionRequireInteraction?: boolean
      researchContactRequireInteraction?: boolean
    }
  ): boolean {
    if (!canonical) {
      return true
    }
    if (options.nyRetentionRequireInteraction) {
      const ny = canonical.patient?.newYorkSampleRetentionOptIn
      if (ny !== true && ny !== false) {
        return false
      }
    }
    if (options.researchContactRequireInteraction) {
      const research = canonical.patient?.researchContactConsent
      if (research !== true && research !== false) {
        return false
      }
    }
    return true
  }

  /**
   * Comparator incidental choice for one parent row (used by order summary cards).
   * When IF is applicable and the slot is active for the test, a fully complete row must include an explicit choice.
   */
  static canonicalParentIncidentalChoiceComplete(
    parent: CanonicalParentInfo | null | undefined,
    options: { incidentalApplicable: boolean; slotActive: boolean }
  ): boolean {
    if (!options.incidentalApplicable || !options.slotActive) {
      return true
    }
    if (!parent) {
      return true
    }
    if (!this.isParentDataFullyComplete(parent as ParentFieldShape)) {
      return true
    }
    const inc = parent.includeIncidentalFindings
    return inc === true || inc === false
  }

  /**
   * C1.1: comparator-shaped sibling of
   * {@link canonicalParentIncidentalChoiceComplete}. Reads the IF flag from
   * `Comparator.secondaryConsent.includeIncidentalFindings` instead of the
   * legacy slot's flat `includeIncidentalFindings`. Used by the in-progress
   * page's per-parent section-complete useMemos which now resolve via
   * `readParent` (preferring `comparators[]`).
   */
  static canonicalComparatorIncidentalChoiceComplete(
    comparator: Comparator | null | undefined,
    options: { incidentalApplicable: boolean; slotActive: boolean }
  ): boolean {
    if (!options.incidentalApplicable || !options.slotActive) {
      return true
    }
    if (!comparator) {
      return true
    }
    if (!this.isParentDataFullyComplete(this.comparatorAsParentData(comparator))) {
      return true
    }
    const inc = comparator.secondaryConsent?.includeIncidentalFindings
    return inc === true || inc === false
  }

  /**
   * Full canonical canSubmit check — mirrors the per-section checks in OrderStart.canSubmit
   * useMemo but reads directly from a CanonicalOrderPayload.
   */
  static isCanonicalOrderSubmittable(
    canonical: CanonicalOrderPayload | null | undefined,
    options: {
      parentSamplesRequired: number
      selectedComparatorRelationships?: readonly FamilyMemberRelationship[]
      requireClinicalSummary?: boolean
      requirePhenotypes?: boolean
      requireFamilyHistory?: boolean
      familyHistoryTouched?: boolean
      familyHistoryIncluded?: boolean
      /** When true, incidental findings opt-in/opt-out is required for proband and complete comparator rows. */
      incidentalFindingsApplicable?: boolean
      /** When true, the proband's NY sample-retention consent must be an explicit true/false (requireInteraction gate). */
      nyRetentionRequireInteraction?: boolean
      /** When true, the proband's research-contact consent must be an explicit true/false (requireInteraction gate). */
      researchContactRequireInteraction?: boolean
      /** Resolved consent flow (e.g. PRIMARY); proband-only guardian rules when set to PRIMARY. */
      effectiveConsentFlow?: string | null
      /**
       * R5 decoupled completion: the SKU tier no longer maps to specific
       * biological mother/father slots, and comparator rows (including parents)
       * are validated separately by the caller via
       * {@link isCanonicalComparatorComplete}. When true:
       *  - skip the legacy mother/father parent-section gate (which is stricter
       *    than the R5 comparator editor — e.g. it requires DOB), and
       *  - accept a primary-guardian comparator as satisfying the standalone
       *    guardian-contact requirement (R5 nulls the legacy `guardianContact`
       *    slot once a comparator is the primary guardian).
       */
      decoupledComparators?: boolean
    }
  ): boolean {
    if (!canonical) return false
    const hasTestType = Boolean(canonical.order?.selectedOption?.trim())
    if (!hasTestType) return false
    if (options.parentSamplesRequired === 1) {
      const { mSel: m, fSel: f } = parentSelectionFlags(options.selectedComparatorRelationships)
      if (!m && !f) return false
      if (m && f) return false
    }
    const p = canonical.patient
    const patientComplete = Boolean(
      p?.firstName?.trim() &&
      p?.lastName?.trim() &&
      p?.dateOfBirth?.trim() &&
      p?.sex?.trim() &&
      p?.mrn?.trim() &&
      p?.collectionType?.trim()
    )
    if (!patientComplete) return false
    if (
      !this.isCanonicalIncidentalFindingsSubmittable(canonical, {
        incidentalApplicable: options.incidentalFindingsApplicable === true,
        parentSamplesRequired: options.parentSamplesRequired,
        selectedComparatorRelationships: options.selectedComparatorRelationships,
      })
    ) {
      return false
    }
    if (
      !this.isCanonicalSecondaryConsentSubmittable(canonical, {
        nyRetentionRequireInteraction: options.nyRetentionRequireInteraction === true,
        researchContactRequireInteraction: options.researchContactRequireInteraction === true,
      })
    ) {
      return false
    }
    if (options.requireClinicalSummary && !canonical.clinical?.clinicalNote?.trim()) return false
    if (options.requirePhenotypes) {
      const hasPhenotypes =
        Array.isArray(canonical.reason?.clinicalDiagnosis) && canonical.reason.clinicalDiagnosis.length > 0
      if (!hasPhenotypes) return false
    }
    if (options.requireFamilyHistory) {
      const touched = options.familyHistoryTouched ?? false
      const included = options.familyHistoryIncluded ?? true
      if (!touched) return false
      if (included && !canonical.family?.familyHistory?.trim()) return false
    }
    // Legacy parent-section gate. Skipped under R5 decoupled completion, where
    // the caller validates each comparator row (parents included) via
    // isCanonicalComparatorComplete — the legacy gate is stricter (e.g. demands
    // DOB) and would silently block submit despite the alerts being clear.
    // C1.2: `isCanonicalParentSectionComplete` now reads through
    // `resolveParentForRead`, so this gate sees the comparator row (with its
    // secondaryConsent / specimen / clinical metadata) for both new orders and
    // legacy-slot-only orders — no behavior change at this call site.
    if (!options.decoupledComparators) {
      const motherOk = this.isCanonicalParentSectionComplete('mother', canonical, options.parentSamplesRequired)
      const fatherOk = this.isCanonicalParentSectionComplete('father', canonical, options.parentSamplesRequired)
      if (!motherOk || !fatherOk) return false
    }

    if (
      options.parentSamplesRequired > 0 &&
      !this.isCanonicalComparatorResearchContactSatisfied(canonical, {
        parentSamplesRequired: options.parentSamplesRequired,
        selectedComparatorRelationships: options.selectedComparatorRelationships,
      })
    ) {
      return false
    }

    // Research contact opt-in or PRIMARY consent flow requires guardian /
    // legal-representative contact for CareBus order_created (provider-api RFD
    // path). Legacy (single-column) orders only enforced this for the
    // proband-only case (`parentSamplesRequired === 0`), leaning on tested
    // parents to supply the channel on family orders. Under R5 decoupled
    // completion the guardian contact is a patient-level field surfaced in every
    // sidenav order, so the requirement fires regardless of family structure —
    // whenever the proband opts into research contact, guardian contact is not
    // optional (satisfied by the legacy slot OR a primary-guardian comparator).
    const flow = (options.effectiveConsentFlow ?? '').trim()
    const probandResearchOptIn = canonical.patient?.researchContactConsent === true
    const needsStandaloneGuardian = probandResearchOptIn || flow === 'PRIMARY'
    const enforceStandaloneGuardian = options.decoupledComparators === true || options.parentSamplesRequired === 0
    if (enforceStandaloneGuardian && needsStandaloneGuardian) {
      // A reachable channel is only required when research contact is obligated
      // (proband opt-in); a PRIMARY consent flow alone needs guardian identity,
      // not a channel — matching the demographics editor so save/submit agree.
      if (
        !this.standaloneGuardianContactSatisfied(canonical, {
          decoupledComparators: options.decoupledComparators,
          requireContactChannel: probandResearchOptIn,
        })
      ) {
        return false
      }
    }

    return true
  }

  /**
   * Whether the standalone guardian-contact requirement (triggered by proband
   * research-contact opt-in or a PRIMARY consent flow) is satisfied. The legacy
   * `guardianContact` slot suffices; under R5 decoupled completion a
   * primary-guardian comparator also satisfies it, since that comparator owns
   * the guardian info once the legacy slot is nulled.
   *
   * A *contact channel* (email/phone) is only required when `requireContactChannel`
   * is set — i.e. the proband (or the guardian comparator) opted into research
   * contact, so the guardian must be reachable for CareBus follow-up. Absent that
   * opt-in (e.g. a PRIMARY consent flow with no research opt-in), a name-only
   * primary-guardian comparator satisfies the requirement, matching what the
   * demographics editor actually requires — so save and submit can't disagree.
   * Shared by the submit gate and both pages' "needs attention" indicators so
   * they can't diverge.
   */
  static standaloneGuardianContactSatisfied(
    canonical: CanonicalOrderPayload | null | undefined,
    options?: { decoupledComparators?: boolean; requireContactChannel?: boolean }
  ): boolean {
    if (!canonical) return false
    if (guardianContactHasSubstantiveContact(canonical.guardianContact)) return true
    if (options?.decoupledComparators !== true) return false
    // With decoupled comparators the primary-guardian comparator carries the
    // guardian info. Require a reachable channel only when research contact is
    // obligated; otherwise a name-bearing guardian row is enough.
    return options?.requireContactChannel === true
      ? this.primaryGuardianComparatorHasContactChannel(canonical)
      : this.primaryGuardianComparatorHasContact(canonical)
  }

  /**
   * R5 (decoupled completion): is a single `comparators[]` row complete for
   * submission, for a comparator of ANY relationship (biological parent OR
   * "other comp")? This is the per-comparator analogue of the parent-slot
   * completeness used by {@link isCanonicalOrderSubmittable}; the R5 flow no
   * longer maps the SKU tier to specific biological mother/father slots, so a
   * trio made of (e.g.) Mother + Grandfather must validate each comparator row
   * on its own terms instead of demanding a biological Father.
   *
   * The required-field set mirrors the R5 comparator section editor exactly
   * (relationship + name + a sample type for a testing comparator, a contact
   * channel when the comparator opted into research contact). DOB and MRN are
   * not required in R5 (the editor renders them optional), matching what the
   * modal accepts so "the modal saved it" and "the page is complete" cannot
   * diverge. When incidental findings are org-applicable, the Consent section
   * (always required for a comparator) needs an explicit opt-in/opt-out.
   *
   * When `probandResearchContactConsent` is set, the primary-guardian comparator
   * (any relationship, testing or not) must additionally supply a contact
   * channel — it owns the proband's research-contact channel for CareBus.
   */
  static isCanonicalComparatorComplete(
    comparator: Comparator | null | undefined,
    options: {
      incidentalApplicable?: boolean
      mrnRequired?: boolean
      probandResearchContactConsent?: boolean
      /** When true, the comparator's NY sample-retention consent must be an explicit true/false. */
      nyRetentionRequireInteraction?: boolean
      /** When true, the comparator's research-contact consent must be an explicit true/false. */
      researchContactRequireInteraction?: boolean
    }
  ): boolean {
    if (!comparator) return false
    const isTesting = comparator.isTestingComparator !== false
    const d = comparator.demographic
    const required = requiredComparatorFields({
      isTesting,
      enableSampleType: true,
      mrnRequired: options.mrnRequired === true,
      researchContactRequired: comparator.secondaryConsent?.researchContactConsent === true,
      primaryGuardianResearchContactRequired:
        options.probandResearchContactConsent === true && comparator.isPrimaryGuardian === true,
      dateOfBirth: d?.dateOfBirth,
    })
    if (!comparator.relationship || String(comparator.relationship).trim() === '') return false
    if (required.has('firstName') && !d?.firstName?.trim()) return false
    if (required.has('lastName') && !d?.lastName?.trim()) return false
    if (required.has('sex') && !d?.sex?.trim()) return false
    if (required.has('dateOfBirth') && !d?.dateOfBirth?.trim()) return false
    if (required.has('sampleType') && !comparator.specimen?.collectionType?.trim()) return false
    if (required.has('mrn') && !d?.mrn?.trim()) return false
    if (required.has('contactChannel') && !d?.email?.trim() && !d?.phone?.trim()) return false
    if (!this.comparatorSecondaryConsentInteractionsComplete(comparator, options)) return false
    return true
  }

  /**
   * Whether a comparator's secondary consents that require explicit interaction
   * have been answered. Incidental uses its existing applicability gate (an
   * explicit include true/false); NY sample retention and research contact are
   * gated only when their config sets `requireInteraction: true` (already ANDed
   * with applicability by the caller), in which case the stored value must be an
   * explicit `true`/`false` rather than `null`/`undefined` (untouched).
   */
  private static comparatorSecondaryConsentInteractionsComplete(
    comparator: Comparator,
    options: {
      incidentalApplicable?: boolean
      nyRetentionRequireInteraction?: boolean
      researchContactRequireInteraction?: boolean
    }
  ): boolean {
    return this.comparatorConsentMissing(comparator, options).length === 0
  }

  /**
   * User-facing labels of the secondary consents a comparator still has to
   * answer (see {@link comparatorSecondaryConsentInteractionsComplete}). Empty
   * when the Additional Options section is complete.
   */
  static comparatorConsentMissing(
    comparator: Comparator,
    options: {
      incidentalApplicable?: boolean
      nyRetentionRequireInteraction?: boolean
      researchContactRequireInteraction?: boolean
    }
  ): string[] {
    const sc = comparator.secondaryConsent
    const answered = (v: boolean | null | undefined) => v === true || v === false
    const missing: string[] = []
    if (options.incidentalApplicable && !answered(sc?.includeIncidentalFindings)) missing.push('incidental findings')
    if (options.nyRetentionRequireInteraction && !answered(sc?.newYorkSampleRetentionOptIn)) {
      missing.push('NY sample retention')
    }
    if (options.researchContactRequireInteraction && !answered(sc?.researchContactConsent)) {
      missing.push('research contact')
    }
    return missing
  }

  /**
   * Per-section completeness for the R5 comparator detail view. The overall
   * {@link isCanonicalComparatorComplete} predicate is intentionally split so a
   * single incomplete section doesn't flag all three cards. Fields map to the
   * card that actually renders them:
   *   - Demographics: relationship, first/last name, DOB, sample type, MRN,
   *     and the research-contact channel (email/phone lives here).
   *   - Clinical History: only phenotypic trait, and only when clinical
   *     identity is required (defaults to complete otherwise).
   *   - Consent: an explicit incidental-findings choice when org-applicable.
   */
  static comparatorDemographicsComplete(
    comparator: Comparator | null | undefined,
    options: { mrnRequired?: boolean; probandResearchContactConsent?: boolean }
  ): boolean {
    return comparator != null && this.comparatorDemographicsMissing(comparator, options).length === 0
  }

  /**
   * User-facing labels of the required Demographics fields a comparator is
   * still missing, in form order. Empty when the section is complete. Labels
   * only — never field values (PHI).
   */
  static comparatorDemographicsMissing(
    comparator: Comparator,
    options: { mrnRequired?: boolean; probandResearchContactConsent?: boolean }
  ): string[] {
    const isTesting = comparator.isTestingComparator !== false
    const d = comparator.demographic
    const required = requiredComparatorFields({
      isTesting,
      enableSampleType: true,
      mrnRequired: options.mrnRequired === true,
      researchContactRequired: comparator.secondaryConsent?.researchContactConsent === true,
      primaryGuardianResearchContactRequired:
        options.probandResearchContactConsent === true && comparator.isPrimaryGuardian === true,
      dateOfBirth: d?.dateOfBirth,
    })
    const missing: string[] = []
    if (!comparator.relationship || String(comparator.relationship).trim() === '') missing.push('relationship')
    if (required.has('firstName') && !d?.firstName?.trim()) missing.push('first name')
    if (required.has('lastName') && !d?.lastName?.trim()) missing.push('last name')
    if (required.has('sex') && !d?.sex?.trim()) missing.push('sex assigned at birth')
    if (required.has('dateOfBirth') && !d?.dateOfBirth?.trim()) missing.push('date of birth')
    if (required.has('sampleType') && !comparator.specimen?.collectionType?.trim()) missing.push('sample type')
    if (required.has('mrn') && !d?.mrn?.trim()) missing.push('MRN')
    if (required.has('contactChannel') && !d?.email?.trim() && !d?.phone?.trim()) missing.push('email or phone')
    return missing
  }

  /**
   * Clinical History section completeness. Phenotypic trait defaults to
   * `Unknown` (treated as complete), so this only fails when clinical identity
   * is required and the trait is genuinely missing. The R5 detail view does not
   * require clinical identity, so this is effectively always complete there.
   */
  static comparatorClinicalComplete(
    comparator: Comparator | null | undefined,
    options?: { requiresClinicalIdentity?: boolean }
  ): boolean {
    if (!comparator) return false
    if (options?.requiresClinicalIdentity !== true) return true
    const trait = comparator.phenotypicTrait
    return trait !== null && trait !== undefined && String(trait).trim() !== ''
  }

  /**
   * Consent section completeness: when incidental findings are org-applicable,
   * the comparator must have an explicit opt-in/opt-out. Other consents are
   * optional, so an inapplicable incidental config is always complete.
   */
  static comparatorConsentComplete(
    comparator: Comparator | null | undefined,
    options: {
      incidentalApplicable?: boolean
      nyRetentionRequireInteraction?: boolean
      researchContactRequireInteraction?: boolean
    }
  ): boolean {
    if (!comparator) return false
    return this.comparatorSecondaryConsentInteractionsComplete(comparator, options)
  }
}
